import React, { useEffect, useState, useRef } from 'react';
import { ownerPortal } from '../services/api';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  submitted:    'bg-yellow-100 text-yellow-800',
  acknowledged: 'bg-blue-100 text-blue-800',
  in_progress:  'bg-purple-100 text-purple-800',
  completed:    'bg-green-100 text-green-800',
  closed:       'bg-gray-100 text-gray-600',
};

const PRIORITY_COLORS = {
  low:    'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high:   'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const PRIORITY_ICONS = { low: '🟢', medium: '🔵', high: '🟠', urgent: '🔴' };
const CONFIDENCE_COLORS = { high: 'text-green-600', medium: 'text-yellow-600', low: 'text-gray-400' };

const CATEGORIES = [
  'plumbing', 'electrical', 'hvac', 'appliance', 'structural',
  'pest_control', 'common_area', 'parking', 'other',
];

const INITIAL_FORM = { title: '', description: '', category: 'other', priority: 'medium', photos: [] };

function AIAnalysisCard({ analysis, onApply }) {
  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="text-lg">🤖</span>
        <span className="font-semibold text-violet-900 text-sm">AI Damage Assessment</span>
        <span className={`ml-auto text-xs font-medium ${CONFIDENCE_COLORS[analysis.confidence]}`}>
          {analysis.confidence} confidence
        </span>
      </div>

      {/* What AI sees */}
      {analysis.what_ai_sees && (
        <p className="text-xs text-violet-700 italic leading-relaxed">"{analysis.what_ai_sees}"</p>
      )}

      {/* Key findings */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white rounded-lg p-2.5 border border-violet-100">
          <p className="text-xs text-gray-500 mb-0.5">Damage Type</p>
          <p className="text-sm font-semibold text-gray-800">{analysis.damage_type}</p>
        </div>
        <div className="bg-white rounded-lg p-2.5 border border-violet-100">
          <p className="text-xs text-gray-500 mb-0.5">Urgency</p>
          <p className="text-sm font-semibold text-gray-800 capitalize">
            {PRIORITY_ICONS[analysis.priority]} {analysis.priority}
          </p>
        </div>
        <div className="bg-white rounded-lg p-2.5 border border-violet-100 col-span-2">
          <p className="text-xs text-gray-500 mb-0.5">Estimated Repair Cost (CAD)</p>
          <p className="text-sm font-semibold text-gray-800">
            ${analysis.cost_low.toLocaleString()} – ${analysis.cost_high.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Urgency reason */}
      {analysis.urgency_reason && (
        <p className="text-xs text-gray-600 leading-relaxed">
          <span className="font-medium">Why: </span>{analysis.urgency_reason}
        </p>
      )}

      {/* Recommended action */}
      {analysis.recommended_action && (
        <div className="bg-white border border-violet-100 rounded-lg px-3 py-2 text-xs text-gray-700">
          <span className="font-medium text-violet-800">Recommended: </span>
          {analysis.recommended_action}
        </div>
      )}

      {/* Apply button */}
      <button
        type="button"
        onClick={onApply}
        className="w-full py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors"
      >
        Apply AI Suggestions to Form
      </button>
    </div>
  );
}

export default function OwnerMaintenance() {
  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [previews, setPreviews]   = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiResult, setAiResult]   = useState(null);
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const load = () => {
    ownerPortal.maintenance()
      .then((res) => setRequests(res.data))
      .catch(() => toast.error('Failed to load requests'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    set('photos', files);
    setPreviews(files.map((f) => URL.createObjectURL(f)));
    setAiResult(null);

    // Auto-analyze first photo
    setAnalyzing(true);
    try {
      const res = await ownerPortal.analyzePhoto(files[0]);
      setAiResult(res.data);
    } catch {
      toast.error('AI analysis failed — you can still submit manually');
    } finally {
      setAnalyzing(false);
    }
  };

  const applyAiSuggestions = () => {
    if (!aiResult) return;
    setForm((p) => ({
      ...p,
      category: aiResult.category || p.category,
      priority: aiResult.priority || p.priority,
      title: p.title || aiResult.damage_type,
      description: p.description || aiResult.what_ai_sees,
    }));
    toast.success('AI suggestions applied!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('category', form.category);
      fd.append('priority', form.priority);
      if (aiResult) fd.append('ai_analysis', JSON.stringify(aiResult));
      if (form.photos.length > 0) {
        form.photos.forEach((p) => fd.append('photos', p));
        await ownerPortal.submitMaintenance(fd, true);
      } else {
        await ownerPortal.submitMaintenance({
          title: form.title,
          description: form.description,
          category: form.category,
          priority: form.priority,
        });
      }
      toast.success('Maintenance request submitted!');
      setForm(INITIAL_FORM);
      setPreviews([]);
      setAiResult(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setAiResult(null);
    setPreviews([]);
    setForm(INITIAL_FORM);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Maintenance Requests</h1>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Request
        </button>
      </div>

      {/* New request form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Submit Maintenance Request</h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">

              {/* ── Photo upload first (triggers AI) ── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📷 Upload Photo{' '}
                  <span className="text-violet-600 font-semibold">← AI will analyze it instantly</span>
                </label>

                {/* Drop zone */}
                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-violet-300 rounded-lg cursor-pointer hover:border-violet-500 hover:bg-violet-50 transition-colors">
                  <svg className="w-6 h-6 text-violet-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm text-violet-600 font-medium">Tap to attach photo</p>
                  <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={handlePhotos} className="hidden" />
                </label>

                {/* Camera */}
                <label className="mt-2 flex items-center justify-center gap-2 w-full border border-violet-200 rounded-lg py-2.5 text-sm text-violet-700 cursor-pointer hover:bg-violet-50 transition-colors">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Take Photo Now
                  <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhotos} className="hidden" />
                </label>

                {/* Photo previews */}
                {previews.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {previews.map((url, i) => (
                      <img key={i} src={url} alt="" className="w-16 h-16 object-cover rounded-lg border-2 border-violet-200" />
                    ))}
                  </div>
                )}
              </div>

              {/* ── AI analyzing spinner ── */}
              {analyzing && (
                <div className="flex items-center gap-3 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                  <svg className="animate-spin w-5 h-5 text-violet-600 shrink-0" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-violet-800">AI is analyzing your photo…</p>
                    <p className="text-xs text-violet-600">Estimating damage type, urgency & repair cost</p>
                  </div>
                </div>
              )}

              {/* ── AI result card ── */}
              {aiResult && !analyzing && (
                <AIAnalysisCard analysis={aiResult} onApply={applyAiSuggestions} />
              )}

              {/* ── Form fields ── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issue Title <span className="text-red-500">*</span>
                </label>
                <input required value={form.title} onChange={(e) => set('title', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                  placeholder="e.g. Leaking pipe under kitchen sink" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={form.category} onChange={(e) => set('category', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 outline-none">
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select value={form.priority} onChange={(e) => set('priority', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 outline-none">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={(e) => set('description', e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none resize-none"
                  placeholder="Describe the issue in detail. When did it start? How severe is it?" />
              </div>

              {form.priority === 'urgent' && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                  ⚠️ For urgent emergencies (fire, flood, gas leak), call 911 immediately. Then contact your strata manager directly.
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeForm}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 font-medium">
                  Cancel
                </button>
                <button type="submit" disabled={submitting || analyzing}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-60">
                  {submitting ? 'Submitting…' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Request list */}
      {requests.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm py-16 text-center">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          </svg>
          <p className="text-gray-500">No maintenance requests yet</p>
          <button onClick={() => setShowForm(true)}
            className="mt-3 text-blue-600 hover:underline text-sm font-medium">
            Submit your first request
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <div key={req.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">{req.title}</h3>
                  <p className="text-sm text-gray-500 mt-0.5 capitalize">
                    {req.category?.replace('_', ' ')} ·{' '}
                    {new Date(req.created_at).toLocaleDateString('en-CA', {
                      year: 'numeric', month: 'short', day: 'numeric',
                    })}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLORS[req.status] || 'bg-gray-100 text-gray-600'}`}>
                    {req.status.replace('_', ' ')}
                  </span>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${PRIORITY_COLORS[req.priority] || ''}`}>
                    {PRIORITY_ICONS[req.priority]} {req.priority}
                  </span>
                </div>
              </div>

              {req.description && (
                <p className="text-sm text-gray-600 mt-3 border-t border-gray-100 pt-3">
                  {req.description}
                </p>
              )}

              {req.photo_urls?.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {req.photo_urls.map((url, i) => (
                    <img key={i} src={url} alt="" className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                  ))}
                </div>
              )}

              {req.manager_notes && (
                <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5 text-sm">
                  <p className="font-medium text-blue-800 text-xs mb-1">Manager Update:</p>
                  <p className="text-blue-700">{req.manager_notes}</p>
                </div>
              )}

              {req.work_order_id && (
                <p className="text-xs text-gray-400 mt-2">Work Order #{req.work_order_id} created</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
